import * as mobx from 'mobx';
import { IntlShape } from 'react-intl';
import * as IoC from 'typescript-ioc';
import { IErrorParsing } from '../../../../../../infrastructure/error-parsing';
import { ISubWindowsService } from '../../../../../../infrastructure/sub-windows';
import { showNotification, handleApiException} from '../../../../../global/modal/alerts-host/alert-templates';
import { LONG_RICH_TEXT_LENGTH } from '../../../../../../constants/common.constants';
import {ICustomCssDAO} from '../../../../../../api/custom-css.dao';
import {CUSTOM_CSS_LONG_ERROR_MESSAGE} from '../../../../../../constants/validation-errors';
import TrackingServiceProvider from '../../../../../../tracking-service/tracking-service-provider';
import { convertBoolTypeForMixPanel, TRACKING_EVENTS } from '../../../../../../tracking-service/tracking-values';
import { IReactIntlProvider } from '../../../../../../infrastructure/react-intl-provider';

class CustomCssFormVM {
    @mobx.observable
    public customCss: string;
    @mobx.observable
    public saving: boolean;

    private initialCss: string;

    private readonly accountId: number;
    private readonly eventId: number;

    constructor(
        public props: { accountId: number; eventId: number; customCss: string; updateCustomCss: (customCss: string) => void },
        private readonly customCssDAO: ICustomCssDAO = IoC.Container.get(ICustomCssDAO),
        private readonly errorParsingService: IErrorParsing = IoC.Container.get(IErrorParsing),
        @IoC.Inject private readonly subWindowService: ISubWindowsService = IoC.Container.get(ISubWindowsService),
        @IoC.Inject private readonly trackingService: TrackingServiceProvider = IoC.Container.get(TrackingServiceProvider),
        @IoC.Inject private readonly intlProvider: IReactIntlProvider = IoC.Container.get(IReactIntlProvider)
    ) {
        this.accountId = props.accountId;
        this.eventId = props.eventId;
        this.customCss = props.customCss;
        this.initialCss = props.customCss;
    }

    public get intl(): IntlShape { return this.intlProvider.getIntl(); }

    public getCustomCss(): string {
        return this.customCss;
    }

    public getCustomCssValidationError = () : string | null => {
        return this.customCss.length > LONG_RICH_TEXT_LENGTH ? CUSTOM_CSS_LONG_ERROR_MESSAGE : null;
    };

    @mobx.action
    public setCustomCss = (value: string) => {
        this.customCss = value;
    };

    private setInitialCss = (value: string) => {
        this.initialCss = value;
    };

    @mobx.action
    public handleSubmitCustomCssForm = async () => {
        if (this.saving || this.getCustomCssValidationError()) {
            return false;
        }

        try {
            this.saving = true;
            await this.customCssDAO.updateEventCustomCss({
                accountId: this.accountId,
                eventId: this.eventId,
                customCss: this.customCss
            });
            this.props.updateCustomCss(this.customCss);
            showNotification(
                this.intl.formatMessage({
                    id: 'oe.websiteTab.customCssUpdated',
                    description: 'Notification about updated custom css',
                    defaultMessage: 'Custom CSS updated'
                })
            );
            this.trackingService.get().track(TRACKING_EVENTS.CUSTOM_CSS_CONFIGURATION, {
                'Custom CSS': convertBoolTypeForMixPanel(this.customCss !== this.initialCss)
            });
            this.setInitialCss(this.customCss);
            return true;
        } catch (error) {
            handleApiException(error);
            return false;
        } finally {
            mobx.runInAction(() => {
                this.saving = false;
            });
        }
    };
}

export { CustomCssFormVM };
